import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { HeartHandshake, Brain, Building2, Mail, Lock, User, Eye, EyeOff, AlertCircle, Palette } from 'lucide-react';

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Destino al que volver después del login. Solo aceptamos rutas internas para evitar open-redirect.
  const redirectParam = searchParams.get('redirect') || '';
  const safeRedirect = redirectParam.startsWith('/') && !redirectParam.startsWith('//') ? redirectParam : '';

  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });

  React.useEffect(function() {
    if (auth.isAuthenticated) {
      navigate(safeRedirect || '/dashboard');
    }
  }, [auth.isAuthenticated, navigate, safeRedirect]);

  function handleChange(e) {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'register') {
        if (!formData.name || formData.name.trim().length < 2) {
          setError('Por favor ingresa tu nombre completo');
          setLoading(false);
          return;
        }
        if (!formData.email || !formData.email.includes('@')) {
          setError('Por favor ingresa un email válido');
          setLoading(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Las contraseñas no coinciden');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setError('La contraseña debe tener al menos 6 caracteres');
          setLoading(false);
          return;
        }
        await auth.registerWithEmail(formData.email.trim().toLowerCase(), formData.password, formData.name.trim());
      } else {
        await auth.loginWithEmail(formData.email.trim().toLowerCase(), formData.password);
      }
      navigate(safeRedirect || '/dashboard');
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || 'Error inesperado. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16 flex items-center justify-center min-h-[80vh]">
        <Card className="w-full max-w-md mx-4 bg-slate-900 border-slate-800">
          <CardContent className="p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-xl bg-emerald-500 mx-auto mb-4 flex items-center justify-center shadow-glow">
                <HeartHandshake className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-50 mb-2">
                {mode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
              </h1>
              <p className="text-slate-400">
                {mode === 'login' 
                  ? 'Accede a tu cuenta para continuar' 
                  : 'Regístrate para comenzar'}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Google Login Button */}
            <Button 
              onClick={auth.loginWithGoogle}
              variant="outline"
              className="w-full rounded-lg py-6 border-slate-700 hover:bg-slate-800 hover:border-slate-600 text-slate-300 mb-4" 
              data-testid="google-login-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar con Google
            </Button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-slate-900 text-slate-500">o continuar con email</span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              {mode === 'register' && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300">Nombre</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Tu nombre"
                      value={formData.name}
                      onChange={handleChange}
                      required={mode === 'register'}
                      className="pl-10 bg-slate-800 border-slate-700 text-slate-50 placeholder:text-slate-500 focus:border-emerald-500"
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-slate-800 border-slate-700 text-slate-50 placeholder:text-slate-500 focus:border-emerald-500"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="pl-10 pr-10 bg-slate-800 border-slate-700 text-slate-50 placeholder:text-slate-500 focus:border-emerald-500"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {mode === 'register' && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-300">Confirmar Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required={mode === 'register'}
                      minLength={6}
                      className="pl-10 bg-slate-800 border-slate-700 text-slate-50 placeholder:text-slate-500 focus:border-emerald-500"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
              )}

              <Button 
                type="submit"
                disabled={loading}
                className="w-full rounded-lg py-6 bg-emerald-500 hover:bg-emerald-600 shadow-glow hover:shadow-glow-lg transition-shadow duration-300" 
                data-testid="email-submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Procesando...
                  </span>
                ) : mode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
              </Button>
            </form>

            {/* Toggle Mode */}
            <p className="text-center text-sm text-slate-400 mt-6">
              {mode === 'login' ? (
                <>
                  ¿No tienes cuenta?{' '}
                  <button
                    onClick={() => { setMode('register'); setError(''); }}
                    className="text-emerald-400 hover:text-emerald-300 font-medium"
                    data-testid="switch-to-register"
                  >
                    Regístrate
                  </button>
                </>
              ) : (
                <>
                  ¿Ya tienes cuenta?{' '}
                  <button
                    onClick={() => { setMode('login'); setError(''); }}
                    className="text-emerald-400 hover:text-emerald-300 font-medium"
                    data-testid="switch-to-login"
                  >
                    Inicia sesión
                  </button>
                </>
              )}
            </p>

            <p className="text-center text-xs text-slate-500 mt-4">
              Al continuar, aceptas nuestros términos y condiciones.
            </p>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}

export function SelectRolePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  // Auto-redirect based on existing role (but not for 'patient' which is default)
  // This hook must be called unconditionally
  React.useEffect(function() {
    if (!auth.loading && auth.user?.role && auth.user.role !== 'patient') {
      const role = auth.user.role;
      if (role === 'sales') {
        navigate('/ventas', { replace: true });
      } else if (role === 'admin' || role === 'superadmin') {
        navigate('/admin', { replace: true });
      } else if (role === 'professional') {
        navigate('/professional/dashboard', { replace: true });
      } else if (role === 'center') {
        navigate('/center/dashboard', { replace: true });
      } else if (role === 'workshop') {
        navigate('/workshop/dashboard', { replace: true });
      }
    }
  }, [auth.loading, auth.user?.role, navigate]);

  async function handleSelectRole(role) {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      await auth.registerRole(role);
      if (role === 'patient') {
        navigate('/');
      } else if (role === 'professional') {
        navigate('/professional/dashboard');
      } else if (role === 'center') {
        navigate('/center/register');
      } else if (role === 'workshop') {
        navigate('/workshop/dashboard');
      }
    } catch (e) {
      console.error('Error selecting role:', e);
      const errorMessage = e.response?.data?.detail || 'Error al seleccionar el rol. Por favor, intenta de nuevo.';
      setError(errorMessage);
      // If unauthorized, suggest re-login
      if (e.response?.status === 401) {
        setError('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  // If still loading auth, show spinner
  if (auth.loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!auth.isAuthenticated || !auth.user) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <main className="pt-24 pb-16 flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <p className="text-slate-400 mb-4">Sesión no válida. Por favor, inicia sesión nuevamente.</p>
            <Button onClick={() => navigate('/login')} className="bg-emerald-500 hover:bg-emerald-600">
              Iniciar Sesión
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const userName = auth.user?.name?.split(' ')[0] || 'Usuario';

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16 flex items-center justify-center min-h-[80vh]">
        <div className="w-full max-w-4xl mx-4">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-slate-50 mb-3">¡Hola, {userName}!</h1>
            <p className="text-slate-400 text-lg">¿Cómo te gustaría usar Sin Adicciones?</p>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm max-w-md mx-auto">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card 
              className={`cursor-pointer bg-slate-900 border-slate-800 hover:border-rose-500/50 transition-all duration-300 hover:-translate-y-1 group ${loading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={function() { handleSelectRole('patient'); }} 
              data-testid="select-patient-role"
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-xl bg-rose-500/10 mx-auto mb-4 flex items-center justify-center group-hover:bg-rose-500/20 transition-colors duration-300">
                  <HeartHandshake className="w-8 h-8 text-rose-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-50 mb-2 group-hover:text-rose-400 transition-colors duration-200">Busco Ayuda</h3>
                <p className="text-slate-400 text-xs">Paciente o familiar</p>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer bg-slate-900 border-slate-800 hover:border-emerald-500/50 transition-all duration-300 hover:-translate-y-1 group ${loading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={function() { handleSelectRole('professional'); }} 
              data-testid="select-professional-role"
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-xl bg-emerald-500/10 mx-auto mb-4 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors duration-300">
                  <Brain className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-50 mb-2 group-hover:text-emerald-400 transition-colors duration-200">Soy Profesional</h3>
                <p className="text-slate-400 text-xs">Terapeuta, psicólogo, psiquiatra</p>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer bg-slate-900 border-slate-800 hover:border-sky-500/50 transition-all duration-300 hover:-translate-y-1 group ${loading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={function() { handleSelectRole('center'); }} 
              data-testid="select-center-role"
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-xl bg-sky-500/10 mx-auto mb-4 flex items-center justify-center group-hover:bg-sky-500/20 transition-colors duration-300">
                  <Building2 className="w-8 h-8 text-sky-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-50 mb-2 group-hover:text-sky-400 transition-colors duration-200">Represento un Centro</h3>
                <p className="text-slate-400 text-xs">Centro de rehabilitación o clínica</p>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer bg-slate-900 border-slate-800 hover:border-purple-500/50 transition-all duration-300 hover:-translate-y-1 group ${loading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={function() { handleSelectRole('workshop'); }} 
              data-testid="select-workshop-role"
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-xl bg-purple-500/10 mx-auto mb-4 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors duration-300">
                  <Palette className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-50 mb-2 group-hover:text-purple-400 transition-colors duration-200">Ofrezco un Taller</h3>
                <p className="text-slate-400 text-xs">Taller terapéutico complementario</p>
              </CardContent>
            </Card>
          </div>
          
          {loading && (
            <div className="mt-8 flex justify-center">
              <div className="flex items-center gap-2 text-slate-400">
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Configurando tu perfil...</span>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
